import { api } from '@/config/api';

export interface WhatsappAccount {
  accountUid: string; companyUid: string; businessName: string; profilePicUrl?: string; emailAddress?: string;
  website?: string; aboutStatus?: string; address?: string; businessDescription?: string; businessCategory?: string;
  webhookSubscriptionStatus?: 'pending' | 'subscribed' | 'failed' | 'disconnected';
  webhookSubscribedAt?: string; webhookSubscriptionError?: string; webhookCheckedAt?: string;
  healthError?: string; healthAttemptedAt?: string; connectionUpdatedAt?: string;
  metaProfilePicUrl?: string; profileImagekitFileId?: string;
  profileSyncStatus?: 'pending' | 'synced' | 'failed'; profileSyncError?: string; profileSyncedAt?: string;
  profileDraft?: Record<string, any>; registrationStatus?: 'registered' | 'failed'; registrationError?: string;
  wabaId: string; appId: string; phoneNumberId: string; displayPhoneNumber: string; verifiedName: string;
  apiBaseUrl: string; apiVersion: string;
  qualityRating?: string; messagingLimitTier?: string; healthFetchedAt?: string;
  showReadStatus: boolean; isMetaPaymentEnabled: boolean; isDefault: boolean; isActive: boolean;
  /** Bot-flow retry cap. Defaults: 2 retries within 60 minutes. */
  maxFlowRetries?: number;
  flowRetryWindowMinutes?: number;
  /** Per-account bot mode. Mutually exclusive: scripted flows / AI chat / no auto-reply. */
  botMode?: 'flow' | 'ai_chat' | 'off';
  aiBotConfig?: AiBotConfig;
  /** AI Agent answering this number — its persona, knowledge and handoff rules replace the number's own. */
  aiAgentUid?: string;
  /**
   * Compiled AI system prompt — regenerated server-side on every save of
   * aiBotConfig/botMode. Read-only snapshot for transparency; the runtime
   * rebuilds it with live lead + catalog data on each inbound.
   */
  aiBotCompiledPrompt?: string;
  aiBotPromptUpdatedAt?: string;
  /** Department where leads from this WhatsApp number land first. */
  captureDepartmentUid?: string;
}

export type BotOutcomeCode =
  | 'replied' | 'inbound_failed' | 'flow_handled' | 'account_inactive' | 'bot_off' | 'no_flow_no_fallback' | 'media_unsupported'
  | 'rate_limited' | 'lead_paused' | 'lead_closed' | 'window_closed' | 'opted_out' | 'turn_cap'
  | 'injection_refused' | 'no_business_profile' | 'cost_cap' | 'ai_failed' | 'empty_reply' | 'send_failed';

export interface BotOutcomeRecord { code: BotOutcomeCode; detail?: string; leadUid?: string; at: string }

export type BotCheckStatus = 'ok' | 'warn' | 'fail' | 'info';
export type BotFixAction = 'activate' | 'use_ai_chat' | 'open_ai_settings' | 'open_ai_usage' | 'subscribe_webhook' | 'open_accounts' | 'open_business_profile' | 'test_ai';

export interface BotDiagnostics {
  accountUid: string;
  botMode: 'flow' | 'ai_chat' | 'off';
  checks: Array<{ key: string; status: BotCheckStatus; title: string; detail: string; fix?: BotFixAction }>;
  ai: { provider: string; model: string; scope: 'company' | 'platform' | 'platform-env'; tested: boolean } | null;
  activity: {
    lastWebhookAt?: string; lastInboundAt?: string; lastReplyAt?: string;
    lastOutcome?: BotOutcomeRecord; lastError?: BotOutcomeRecord;
    today: Partial<Record<BotOutcomeCode, number>>;
  };
  checkedAt: string;
}

export interface AiBotPriceItem {
  label: string;
  amount?: number;
  currency?: string;
}

export type UpsellWhenToOffer = 'after_primary' | 'on_match';

export interface AiBotUpsellItem {
  label: string;
  amount?: number;
  currency?: string;
  /** When the bot should consider offering this. Defaults to 'after_primary'. */
  whenToOffer?: UpsellWhenToOffer;
  /** Optional keyword the customer's message must contain for `on_match` offers. */
  trigger?: string;
}

export type AiBotMode = 'intake' | 'order' | 'booking' | 'support';

export interface AiBotConfig {
  instructions?: string;
  language?: string;
  /**
   * Conversation mode. Omit to let the server pick a default from the company's
   * industry. intake = qualify a lead, order = take an order, booking = reserve
   * a slot, support = answer + escalate.
   */
  mode?: AiBotMode;
  modelProvider?: 'openai' | 'claude' | 'qwen' | 'gemini' | 'mistral';
  model?: string;
  maxResponseChars?: number;
  refusalStyle?: 'soft' | 'strict';
  captureLeadEnabled?: boolean;
  greeting?: string;
  /**
   * @deprecated Use Custom Objects (Service / Menu item / Listing / Package) as
   * the source of truth for services. This array is kept for backward compat
   * with tenants who already populated it; new UI does not edit it.
   */
  services?: string[];
  /** @deprecated Same reason as `services` — prices come from Custom Objects now. */
  priceList?: AiBotPriceItem[];
  linkedDataUids?: string[];
  /** Allow the bot to proactively explain features when not directly asked. */
  enableFeaturePitching?: boolean;
  /** Operator-defined slot list the AI captures before lead qualification. */
  requiredFields?: AiBotRequiredField[];
  /** Share services from Custom Objects in chat when the customer asks. */
  showServicesEnabled?: boolean;
  /** Quote prices in chat. When false, money fields are stripped from the prompt. */
  showPricesEnabled?: boolean;
  /** Also answer from the company's Catalog / Menu (available items, current prices). Off by default. */
  useCatalog?: boolean;
  /** Also answer from the company's Knowledge base (AI Agents → Knowledge). Off by default. */
  useKnowledge?: boolean;
  createSalesFollowUp?: boolean;
  createDealsEnabled?: boolean;
  createSupportTicketsEnabled?: boolean;
  /** Master toggle for upsell offers. When off, upsellItems is ignored even if populated. */
  upsellEnabled?: boolean;
  /** Operator-defined upsell offers — bot picks at most ONE per conversation. */
  upsellItems?: AiBotUpsellItem[];
}

export type AiBotFieldType = 'text' | 'email' | 'phone' | 'date' | 'number' | 'choice' | 'multiline';

export interface AiBotRequiredField {
  key: string;
  label: string;
  type: AiBotFieldType;
  required: boolean;
  options?: string[];
  order?: number;
}
export interface HealthInfo { qualityRating?: string; messagingLimitTier?: string; fetchedAt: string; staleSince?: string; error?: string; }

export interface AccountStats { sent: number; delivered: number; read: number; failed: number; }
export type AccountStatsMap = Record<string, AccountStats>;

export interface AccountActivityDay { date: string; inbound: number; outbound: number }
export interface AccountActivity {
  received: number;
  daily: AccountActivityDay[];
  last?: { direction: 'in' | 'out'; at: string; text?: string };
  templates: { total: number; standard: number; carousel: number };
}
export type AccountActivityMap = Record<string, AccountActivity>;

export interface ConnectAccountInput {
  businessName: string;
  wabaId: string;
  appId: string;
  phoneNumberId: string;
  displayPhoneNumber: string;
  verifiedName: string;
  accessToken: string;
  appSecret?: string;
  apiBaseUrl?: string;
  apiVersion?: string;
  profilePicUrl?: string;
  emailAddress?: string;
  website?: string;
  aboutStatus?: string;
  address?: string;
  setAsDefault?: boolean;
  reconnectAccountUid?: string;
}

const BASE = '/slighter_crm/whatsapp-account';

export interface WhatsAppConnectionConfig {
  appId: string; configId: string; graphVersion: string; exchangeAvailable: boolean; available: boolean; reason?: string;
}

export const whatsappAccountService = {
  connectionConfig: async (): Promise<WhatsAppConnectionConfig> => (await api.get(`${BASE}/connection/config`)).data,
  get: async (): Promise<WhatsappAccount> => (await api.get(BASE)).data,
  register: async (accountUid: string, pin: string): Promise<WhatsappAccount> => (await api.post(`${BASE}/${accountUid}/register`, { pin })).data,
  updateProfile: async (p: Partial<WhatsappAccount>): Promise<WhatsappAccount> => (await api.patch(BASE, p)).data,
  health: async (refresh = false, accountUid?: string): Promise<HealthInfo> =>
    (await api.get(`${BASE}/health`, {
      params: { refresh: refresh ? '1' : undefined, accountUid },
    })).data,

  list: async (): Promise<WhatsappAccount[]> => (await api.get(`${BASE}/list`)).data,

  /** Industry-tailored default required-fields list (used by the AI config editor's "Load defaults"). */
  getRequiredFieldDefaults: async (): Promise<AiBotRequiredField[]> =>
    (await api.get(`${BASE}/ai-bot-config/defaults/required-fields`)).data,

  /** Industry-tailored default services / topics chips. */
  getServicesDefaults: async (): Promise<string[]> =>
    (await api.get(`${BASE}/ai-bot-config/defaults/services`)).data,
  stats: async (): Promise<AccountStatsMap> => (await api.get(`${BASE}/stats`)).data,
  getById: async (accountUid: string): Promise<WhatsappAccount> => (await api.get(`${BASE}/${accountUid}`)).data,
  /** Pull the live business profile from Meta into the CRM DB (about, email, websites, address, picture). */
  syncProfile: async (accountUid?: string): Promise<WhatsappAccount> =>
    (await api.post(`${BASE}/sync-profile`, null, { params: accountUid ? { account: accountUid } : {} })).data,
  activity: async (days = 15): Promise<AccountActivityMap> =>
    (await api.get(`${BASE}/activity`, { params: { days } })).data,
  updateProfileById: async (accountUid: string, p: Partial<WhatsappAccount>): Promise<WhatsappAccount> =>
    (await api.patch(`${BASE}/${accountUid}`, p)).data,
  connect: async (input: ConnectAccountInput): Promise<WhatsappAccount> => (await api.post(BASE, input)).data,
  embeddedSignupAttach: async (input: { code: string; wabaId: string; phoneNumberId: string; setAsDefault?: boolean; registrationPin?: string; reconnectAccountUid?: string }): Promise<WhatsappAccount> =>
    (await api.post(`${BASE}/embedded-signup`, input)).data,
  setDefault: async (accountUid: string): Promise<WhatsappAccount> =>
    (await api.post(`${BASE}/${accountUid}/set-default`)).data,
  /** Check + repair the Meta webhook subscription (subscribed_apps) for this WABA. */
  subscribeWebhook: async (accountUid: string): Promise<{
    wabaId: string;
    subscribedBefore: boolean;
    subscribedNow: boolean;
    apps: Array<{ id?: string; name?: string }>;
    error?: string;
  }> => (await api.post(`${BASE}/${accountUid}/subscribe-webhook`)).data,
  setActive: async (accountUid: string, isActive: boolean): Promise<WhatsappAccount> =>
    (await api.patch(`${BASE}/${accountUid}/active`, { isActive })).data,
  remove: async (accountUid: string): Promise<{ ok: true }> => (await api.delete(`${BASE}/${accountUid}`)).data,
  botDiagnostics: async (accountUid: string, live = false): Promise<BotDiagnostics> =>
    (await api.get(`${BASE}/${accountUid}/bot-diagnostics`, { params: live ? { live: 1 } : {} })).data,
};
